import { 
  collection, 
  doc, 
  getDocs, 
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { ClassMember } from '../types/alarm';
import { userDataService } from './userDataService';
import { firebaseReadCounter } from './firebaseReadCounter';
import { GeneralAssignmentConsolidatedService } from './generalAssignmentConsolidatedService';
import { normalizeMemberGroup, withMemberGroup } from '../utils/memberGroupPaths';

// 과제 상태 타입
export type AssignmentStatusValue = 'pending' | 'confirmed' | 'delayed' | 'exempted' | 'excluded';

// 과제 정보 (모든 과제) - 기존 타입과 호환
export interface AssignmentInfo {
  id: string;
  title: string;
  type: 'assignment' | 'project' | 'routine';
  dueDate?: Date;
  subTaskId?: string;
  subTaskTitle?: string; // 하위 과제 제목
  routineAssignmentId?: string;
  routineTitle?: string; // 루틴 과제 제목
  dateId?: string;
  isActive?: boolean;
  assignedStudentIds?: string[];
  hasIncompleteStudents: boolean; // 기존 타입과 호환을 위해 추가
  // 프로젝트 관련 추가 필드들
  projectTitle?: string; // 프로젝트 제목
  projectDueDate?: Date; // 프로젝트 마감일
  projectColor?: string; // 프로젝트 색상
  categoryId?: string; // 카테고리 ID
  color?: string; // 카테고리 색상
  // 포인트 관련 필드들
  basePoints?: number; // 과제 설정 포인트
  // subTaskPoints는 basePoints로 통일됨
  /** 최초 생성일 (정렬용: 왼쪽=최신) */
  createdAt?: Date;
  /** 프로젝트 생성일 (프로젝트 과제만, 프로젝트끼리 정렬용) */
  projectCreatedAt?: Date;
  /** 하위과제 순서 (프로젝트만, 같은 프로젝트 내 정렬용: 마지막=왼쪽) */
  subTaskOrder?: number;
}

// 멤버별 과제 상태
export interface StudentAssignmentStatus {
  studentId: string;
  studentName: string;
  studentNumber: string;
  assignmentStatuses: { [assignmentId: string]: {
    status: AssignmentStatusValue;
    assignmentTitle: string;
    assignmentType: string;
    subTaskId?: string;
    isAssigned: boolean;
  }};
}

/** assignmentStatusKey → studentId → Firestore 원본 멤버 상태 (상세 모달·캐시용) */
export type AssignmentStatusDetailByAssignment = Record<string, Record<string, any>>;

// 테이블 데이터
export interface AssignmentTableData {
  members: ClassMember[];
  allAssignments: AssignmentInfo[];
  studentStatuses: StudentAssignmentStatus[];
  incompleteAssignments: Set<string>;
  incompleteSubTasks: Set<string>;
  categoriesData?: any[];
  projectsData?: any[];
  /** 테이블 로드 시 이미 읽은 멤버별 상세 상태 (추가 네트워크 없이 상세 모달에 사용) */
  statusDetailByAssignment?: AssignmentStatusDetailByAssignment;
}

class AssignmentTableServiceOptimizedV4 {
  /**
   * 🚀 최적화된 과제 테이블 데이터 로드 (V4 - 지연 로딩)
   */
  async loadAssignmentTableData(
    userId: string,
    classId: string,
    assignmentType: 'general' | 'project' | 'routine' = 'general',
    selectedRoutineDate?: Date,
    memberGroup?: string
  ): Promise<AssignmentTableData> {
    console.log(`🚀 V4 최적화된 과제 테이블 데이터 로드 시작 (타입: ${assignmentType})`);
    const startTime = performance.now();
    let queryCount = 0;
    const g = normalizeMemberGroup(memberGroup);
    const generalStatusesCol = withMemberGroup('generalAssignmentStatuses', g);
    
    // Firebase 읽기 횟수 추적 시작
    firebaseReadCounter.setEnabled(true);
    firebaseReadCounter.reset();

    try {
      // 1. 멤버 데이터 로드
      console.log(`🔍 멤버 로드 전 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
      const members = await userDataService.loadClassMembers(userId, classId, g);
      console.log(`🔍 멤버 로드 후 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
      queryCount += 1; // members 컬렉션 1개 쿼리
      console.log(`👥 멤버 로드 완료: ${members.length}명 (쿼리: 1개)`);

      // 2. 선택된 과제 타입만 로드 (지연 로딩)
      let allAssignments: AssignmentInfo[] = [];
      let generalAssignments: AssignmentInfo[] = [];
      let projectAssignments: AssignmentInfo[] = [];
      let routineAssignments: AssignmentInfo[] = [];
      let generalStatusData: Map<string, any> | undefined = undefined;
      let projectStatusData: Map<string, any> | undefined = undefined;
      let routineStatusData: Map<string, any> | undefined = undefined;

      if (assignmentType === 'general') {
        console.log(`🔍 일반 과제 로드 전 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
        const generalResult = await this.loadGeneralAssignments(userId, classId, g);
        console.log(`🔍 일반 과제 로드 후 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
        generalAssignments = generalResult.assignments;
        generalStatusData = generalResult.statusData;
        allAssignments = generalAssignments;
        queryCount += 1;
        console.log(`📚 일반 과제 로드 완료: ${generalAssignments.length}개 (쿼리: 1개)`);
      } else if (assignmentType === 'project') {
        // 🚀 최적화: 프로젝트 과제는 상태 데이터에서 직접 로드 (메타데이터 포함)
        const projectResult = await this.loadProjectAssignmentsFromStatus(userId, classId, g);
        projectAssignments = projectResult.assignments;
        projectStatusData = projectResult.statusData;
        allAssignments = projectAssignments;
        queryCount += 2; // 프로젝트상태(1) + 하위과제상태(1)
        console.log(`📚 프로젝트 과제 로드 완료: ${projectAssignments.length}개 (쿼리: 2개)`);
      } else if (assignmentType === 'routine') {
        // 🚀 최적화: 루틴 과제는 상태 데이터에서 직접 로드 (메타데이터 포함)
        const routineResult = await this.loadRoutineAssignmentsFromStatus(userId, classId, selectedRoutineDate, g);
        routineAssignments = routineResult.assignments;
        routineStatusData = routineResult.statusData;
        allAssignments = routineAssignments;
        queryCount += 2; // 루틴과제(1) + 선택된날짜상태(1)
        console.log(`📚 루틴 과제 로드 완료: ${routineAssignments.length}개 (쿼리: 2개)`);
      }

      // 3. 🚀 최적화: 과제별로 한 번만 상태 조회
      console.log(`🔍 멤버 상태 로드 전 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
      const { studentStatuses, statusDetailByAssignment } = await this.loadStudentStatusesOptimized(
        userId,
        classId,
        members,
        allAssignments,
        generalStatusData,
        projectStatusData,
        routineStatusData,
        generalStatusesCol,
        g
      );
      console.log(`🔍 멤버 상태 로드 후 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
      
      // 일반 과제와 프로젝트 과제의 경우 이미 읽은 데이터를 재사용하므로 추가 쿼리 없음
      if (assignmentType === 'general' || assignmentType === 'project') {
        console.log(`👨‍🎓 멤버 상태 로드 완료: ${studentStatuses.length}명 (쿼리: 0개 - 데이터 재사용)`);
      } else {
        queryCount += allAssignments.length; // 과제별 상태 조회
        console.log(`👨‍🎓 멤버 상태 로드 완료: ${studentStatuses.length}명 (쿼리: ${allAssignments.length}개)`);
      }

      // 4. 미완료 과제 확인 (이미 로드한 상태 데이터에서 처리 - 추가 쿼리 없음)
      const incompleteAssignments = new Set<string>();
      const incompleteSubTasks = new Set<string>();
      
      // loadStudentStatusesOptimized에서 이미 로드한 상태 데이터를 재사용
      // 이 부분은 실제로는 이미 로드된 데이터를 사용하므로 추가 쿼리 없음

      const endTime = performance.now();
      console.log(`✅ V4 최적화된 테이블 데이터 로드 완료: ${endTime - startTime}ms`);
      
      if (assignmentType === 'general') {
        console.log(`📊 총 쿼리 수: ${queryCount}개 (멤버: 1 + 일반과제: 1 + 카테고리: 1)`);
        console.log(`🔍 상세 분석: 멤버(1) + 일반과제(1) + 카테고리(1) = ${queryCount}개`);
      } else if (assignmentType === 'project') {
        console.log(`📊 총 쿼리 수: ${queryCount}개 (멤버: 1 + 프로젝트상태: 1 + 하위과제상태: 1)`);
        console.log(`🔍 상세 분석: 멤버(1) + 프로젝트상태(1) + 하위과제상태(1) = ${queryCount}개`);
      } else if (assignmentType === 'routine') {
        console.log(`📊 총 쿼리 수: ${queryCount}개 (멤버: 1 + 루틴과제: 1 + 오늘날짜상태: 1)`);
        console.log(`🔍 상세 분석: 멤버(1) + 루틴과제(1) + 오늘날짜상태(1) = ${queryCount}개`);
      } else {
        console.log(`📊 총 쿼리 수: ${queryCount}개 (멤버: 1 + ${assignmentType}과제: 1 + 상태: ${allAssignments.length})`);
        console.log(`🔍 상세 분석: 멤버(1) + ${assignmentType}과제(1) + 상태(${allAssignments.length}) = ${queryCount}개`);
      }
      
      // Firebase 읽기 횟수 로그 출력
      firebaseReadCounter.logDetailedAnalysis(`V4 서비스 - ${assignmentType} 과제`);

      // 4. 조건부 로딩 (지연 로딩)
      let categoriesData: any[] = [];
      let projectsData: any[] = [];
      
      if (assignmentType === 'general') {
        // 일반 과제만 로드할 때는 카테고리도 함께 로드
        console.log(`🔍 카테고리 로드 전 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
        categoriesData = await this.loadCategories(userId, classId);
        console.log(`🔍 카테고리 로드 후 Firebase 읽기 횟수: ${firebaseReadCounter.getCount()}`);
        queryCount += 1;
      } else if (assignmentType === 'project') {
        // 🚀 최적화: 프로젝트 과제는 메타데이터가 상태 데이터에 포함되어 있으므로 별도 로드 불필요
        console.log(`🚀 프로젝트 과제 최적화: 메타데이터가 상태 데이터에 포함되어 별도 프로젝트 데이터 로드 생략`);
      }

      // 4. 각 과제에 대해 hasIncompleteStudents 계산 (studentStatuses 로드 후)
      const assignmentsWithIncompleteInfo = await this.calculateIncompleteStudents(
        allAssignments, 
        studentStatuses
      );

      return {
        members,
        allAssignments: assignmentsWithIncompleteInfo,
        studentStatuses,
        incompleteAssignments,
        incompleteSubTasks,
        categoriesData,
        projectsData,
        statusDetailByAssignment
      };
    } catch (error) {
      console.error('V4 최적화된 테이블 데이터 로드 실패:', error);
      firebaseReadCounter.logReadCount('에러 발생');
      throw error;
    }
  }

  /**
   * 🚀 최적화된 멤버 상태 로드 (과제별로 한 번만 쿼리)
   */
  private async loadStudentStatusesOptimized(
    userId: string,
    classId: string,
    members: ClassMember[],
    allAssignments: AssignmentInfo[],
    generalStatusData?: Map<string, any>,
    projectStatusData?: Map<string, any>,
    routineStatusData?: Map<string, any>,
    generalStatusesCol: string = 'generalAssignmentStatuses',
    memberGroup?: string
  ): Promise<{
    studentStatuses: StudentAssignmentStatus[];
    statusDetailByAssignment: AssignmentStatusDetailByAssignment;
  }> {
    console.log(`🔍 loadStudentStatusesOptimized 시작 - 과제 수: ${allAssignments.length}, 일반과제 데이터 존재: ${!!generalStatusData}`);

    // 1. 모든 과제의 상태를 한 번에 조회 (과제 수만큼만 쿼리)
    const assignmentStatusPromises = allAssignments.map(async (assignment) => {
      let statusData: any = null;
      
      try {
        if (assignment.type === 'assignment') {
          // 🚀 최적화: 이미 읽은 데이터 재사용 (중복 쿼리 방지)
          if (generalStatusData && generalStatusData.has(assignment.id)) {
            statusData = generalStatusData.get(assignment.id);
            console.log(`🔄 일반 과제 상태 데이터 재사용: ${assignment.id}`);
          } else {
            // 일반 과제 데이터가 없는 경우에만 쿼리
            const statusRef = doc(
              db,
              'users',
              userId,
              'classes',
              classId,
              generalStatusesCol,
              assignment.id
            );
            const statusSnap = await getDoc(statusRef);
            firebaseReadCounter.increment(1);
            statusData = statusSnap.exists() ? statusSnap.data() : null;
          }
        } else if (assignment.type === 'project' && assignment.subTaskId) {
          // 🚀 최적화: 이미 읽은 데이터 재사용 (중복 쿼리 방지)
          if (projectStatusData && projectStatusData.has(assignment.id)) {
            statusData = projectStatusData.get(assignment.id);
            console.log(`🔄 프로젝트 과제 상태 데이터 재사용: ${assignment.id}`);
          } else {
            // 프로젝트 과제는 통합 문서 구조에서 이미 로드되었으므로 추가 쿼리 불필요
            console.log(`🚀 프로젝트 과제 상태 데이터는 이미 로드됨: ${assignment.id}`);
            statusData = null; // 이미 로드된 데이터 사용
          }
          
          console.log('🔍 프로젝트 과제 상태 조회 결과:', {
            assignmentId: assignment.id,
            subTaskId: assignment.subTaskId,
            documentExists: !!statusData,
            studentStatusesCount: statusData?.studentStatuses?.length || 0,
            studentStatuses: statusData?.studentStatuses?.map((s: any) => ({
              studentId: s.studentId,
              status: s.status,
              earnedPoints: s.earnedPoints
            })) || []
          });
        } else if (assignment.type === 'routine' && assignment.dateId && assignment.routineAssignmentId) {
          // 🚀 최적화: 이미 읽은 데이터 재사용 (중복 쿼리 방지)
          if (routineStatusData && routineStatusData.has(assignment.id)) {
            statusData = routineStatusData.get(assignment.id);
            console.log(`🔄 루틴 과제 상태 데이터 재사용: ${assignment.id}`);
          } else {
            // 루틴 과제는 통합 문서 구조에서 이미 로드되었으므로 추가 쿼리 불필요
            console.log(`🚀 루틴 과제 상태 데이터는 이미 로드됨: ${assignment.id}`);
            statusData = null; // 이미 로드된 데이터 사용
          }
          
          console.log('🔍 루틴 과제 상태 조회 결과:', {
            assignmentId: assignment.id,
            dateId: assignment.dateId,
            documentExists: !!statusData,
            studentStatusesCount: statusData?.studentStatuses?.length || 0,
            studentStatuses: statusData?.studentStatuses?.map((s: any) => ({
              studentId: s.studentId,
              status: s.status,
              earnedPoints: s.earnedPoints
            })) || []
          });
        }
        
        return { assignmentId: assignment.id, statusData };
      } catch (error) {
        console.warn(`과제 상태 로드 실패 (${assignment.id}):`, error);
        return { assignmentId: assignment.id, statusData: null };
      }
    });

    const assignmentStatusResults = await Promise.all(assignmentStatusPromises);
    
    // 2. 과제별 상태 데이터를 Map에 저장 (프로젝트 과제는 하위 과제별로 고유 키 사용)
    const assignmentStatusMap = new Map<string, any[]>();
    const statusDetailByAssignment: AssignmentStatusDetailByAssignment = {};
    assignmentStatusResults.forEach(({ assignmentId, statusData }) => {
      const studentStatuses = statusData?.studentStatuses || [];
      
      // 프로젝트 과제와 루틴 과제의 경우 고유 키 사용
      const assignment = allAssignments.find(a => a.id === assignmentId);
      let uniqueKey = assignmentId;
      
      if (assignment?.type === 'project' && assignment?.subTaskId) {
        uniqueKey = `${assignmentId}-${assignment.subTaskId}`;
      } else if (assignment?.type === 'routine' && assignment?.dateId) {
        uniqueKey = `${assignment.routineAssignmentId}-${assignment.dateId}`; // 루틴 과제는 routineId-dateId 조합을 키로 사용
      }
      
      assignmentStatusMap.set(uniqueKey, studentStatuses);
      const byStudent: Record<string, any> = {};
      studentStatuses.forEach((s: any) => {
        if (s?.studentId) byStudent[s.studentId] = s;
      });
      statusDetailByAssignment[uniqueKey] = byStudent;
    });

    // 3. 멤버별로 상태 정보 구성 (쿼리 없이 메모리에서 처리)
    const studentStatuses: StudentAssignmentStatus[] = members.map((member) => {
      const assignmentStatuses: { [assignmentId: string]: any } = {};

      allAssignments.forEach((assignment) => {
        let status: AssignmentStatusValue = 'pending';
        let isAssigned = false;

        // 프로젝트 과제와 루틴 과제의 경우 고유 키 사용
        let uniqueKey = assignment.id;
        
        if (assignment.type === 'project' && assignment.subTaskId) {
          uniqueKey = `${assignment.id}-${assignment.subTaskId}`;
        } else if (assignment.type === 'routine' && assignment.dateId) {
          uniqueKey = `${assignment.routineAssignmentId}-${assignment.dateId}`; // 루틴 과제는 routineId-dateId 조합을 키로 사용
        }

        // 루틴 과제의 경우 과제를 부여받은 모든 멤버가 표시되어야 함
        if (assignment.type === 'routine') {
          // 루틴 과제는 assignedStudentIds를 확인하여 과제 부여 여부 결정
          isAssigned = assignment.assignedStudentIds?.includes(member.id) || false;
          
          if (isAssigned) {
            const studentStatuses = assignmentStatusMap.get(uniqueKey) || [];
            const studentStatus = studentStatuses.find((s: any) => s.studentId === member.id);
            
            if (studentStatus) {
              status = studentStatus.status || 'pending';
            } else {
              // 상태 데이터가 없는 경우 기본값 'pending' 사용
              status = 'pending';
            }
          }
        } else {
          // 일반 과제와 프로젝트 과제는 기존 로직 유지
          const studentStatuses = assignmentStatusMap.get(uniqueKey) || [];
          const studentStatus = studentStatuses.find((s: any) => s.studentId === member.id);
          
          if (studentStatus) {
            status = studentStatus.status || 'pending';
            isAssigned = true;
          }
        }

        // 디버깅 로그 추가 - 상태 정보를 별도로 출력
        console.log(`🔍 ${member.name}(${member.id}) - ${assignment.title}:`, {
          assignmentType: assignment.type,
          uniqueKey,
          foundStatus: status,
          isAssigned,
          assignedStudentIds: assignment.assignedStudentIds,
          studentStatus: assignment.type === 'routine' ? 
            (assignmentStatusMap.get(uniqueKey) || []).find((s: any) => s.studentId === member.id) : 
            (assignmentStatusMap.get(uniqueKey) || []).find((s: any) => s.studentId === member.id)
        });

        assignmentStatuses[uniqueKey] = {
          status,
          assignmentTitle: assignment.title,
          assignmentType: assignment.type,
          subTaskId: assignment.subTaskId,
          isAssigned,
          memo: ''
        };
      });

      return {
        studentId: member.id,
        studentName: member.name,
        studentNumber: member.number || '',
        assignmentStatuses
      };
    });

    return { studentStatuses, statusDetailByAssignment };
  }

  /**
   * 🚀 최적화된 미완료 과제 확인
   */
  private async checkIncompleteAssignmentsOptimized(
    userId: string,
    classId: string,
    collectionName: string,
    assignmentIds: string[]
  ): Promise<Set<string>> {
    if (assignmentIds.length === 0) return new Set();

    const incompleteAssignments = new Set<string>();

    try {
      // V3 구조에서 각 과제의 상태 문서를 병렬로 확인
      const statusPromises = assignmentIds.map(async (assignmentId) => {
        const statusRef = doc(
          db, 
          'users', 
          userId, 
          'classes', 
          classId, 
          collectionName, 
          assignmentId
        );
        const statusSnap = await getDoc(statusRef);
        
        if (!statusSnap.exists()) {
          return { assignmentId, hasPending: false };
        }
        
        const statusData = statusSnap.data();
        const studentStatuses = statusData.studentStatuses || [];
        
        const hasPending = studentStatuses.some((status: any) => 
          status.status === 'pending'
        );

        return { assignmentId, hasPending };
      });

      const results = await Promise.all(statusPromises);
      
      results.forEach(({ assignmentId, hasPending }) => {
        if (hasPending) {
          incompleteAssignments.add(assignmentId);
        }
      });

      return incompleteAssignments;
    } catch (error) {
      console.error('미완료 과제 확인 실패:', error);
      return incompleteAssignments;
    }
  }

  /**
   * 🚀 최적화된 프로젝트 미완료 상태 확인
   */
  private async checkProjectIncompleteStatusesOptimized(
    userId: string,
    classId: string,
    projectAssignments: AssignmentInfo[],
    projectStatusesCol: string = 'projectAssignmentStatuses'
  ): Promise<Set<string>> {
    const incompleteSubTasks = new Set<string>();

    try {
      // 프로젝트 과제별로 하위 과제 상태 확인
      const projectPromises = projectAssignments.map(async (project) => {
        if (!project.subTaskId) return [];

        const statusRef = doc(
          db,
          'users',
          userId,
          'classes',
          classId,
          projectStatusesCol,
          project.id,
          'subTasks',
          project.subTaskId
        );
        const statusSnap = await getDoc(statusRef);
        
        if (!statusSnap.exists()) {
          return [];
        }
        
        const statusData = statusSnap.data();
        const studentStatuses = statusData.studentStatuses || [];
        
        const hasPending = studentStatuses.some((status: any) => 
          status.status === 'pending'
        );

        return hasPending ? [`${project.id}-${project.subTaskId}`] : [];
      });

      const results = await Promise.all(projectPromises);
      
      results.flat().forEach(key => {
        incompleteSubTasks.add(key);
      });

      return incompleteSubTasks;
    } catch (error) {
      console.error('프로젝트 미완료 상태 확인 실패:', error);
      return incompleteSubTasks;
    }
  }

  /**
   * 일반 과제 로드 (메타데이터 최적화)
   */
  private async loadGeneralAssignments(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<{ assignments: AssignmentInfo[], statusData: Map<string, any> }> {
    // 🚀 새로운 통합 구조 사용: generalAssignmentStatuses 컬렉션만 읽기
    const consolidatedService = new GeneralAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      const assignmentInfos = await consolidatedService.loadAllGeneralAssignments();
      firebaseReadCounter.increment(1); // 통합 문서 읽기는 1회로 계산
      
      const statusDataMap = new Map<string, any>();
      const assignments: AssignmentInfo[] = assignmentInfos.map(info => {
        const assignmentId = info.assignmentId;
        
        // 상태 데이터 저장 (중복 쿼리 방지)
        statusDataMap.set(assignmentId, {
          assignmentId,
          title: info.title,
          dueDate: new Date(info.dueDate),
          assignedStudentIds: info.assignedStudentIds,
          basePoints: info.basePoints,
          studentStatuses: info.studentStatuses
        });
        
        return {
          id: assignmentId,
          title: info.title,
          type: 'assignment' as const,
          dueDate: new Date(info.dueDate),
          isActive: info.isActive,
          assignedStudentIds: info.assignedStudentIds,
          hasIncompleteStudents: false, // 기본값
          categoryId: info.categoryId,
          color: undefined, // 카테고리 색상은 별도 로드 필요
          basePoints: info.basePoints, // 통합 문서에서 기본 포인트 읽기
          createdAt: info.createdAt instanceof Date ? info.createdAt : new Date(info.createdAt as any)
        };
      });
      
      console.log('✅ 새로운 구조: 일반 과제 테이블 로드 완료:', assignments.length);
      return { assignments, statusData: statusDataMap };
    } catch (error) {
      console.error('❌ 새로운 구조: 일반 과제 테이블 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 로드 (통합 문서 구조 사용)
   */
  private async loadProjectAssignmentsFromStatus(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<{ assignments: AssignmentInfo[], statusData: Map<string, any> }> {
    // 🚀 새로운 통합 구조 사용: projectAssignmentStatuses 컬렉션의 projectAssignment-X 문서들에서 로드
    const { ProjectAssignmentConsolidatedService } = await import('./projectAssignmentConsolidatedService');
    const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);
    
    try {
      const projectAssignments = await consolidatedService.loadAllProjectAssignments();
      firebaseReadCounter.increment(1); // 통합 문서 읽기는 1회로 계산
      
      const assignments: AssignmentInfo[] = [];
      const statusDataMap = new Map<string, any>();

      // 각 프로젝트 과제의 하위 과제들을 처리
      for (const projectAssignment of projectAssignments) {
        if (!projectAssignment.isActive || !projectAssignment.assignedStudentIds?.length) {
          continue;
        }

        // 각 하위 과제를 별도의 AssignmentInfo로 생성
        const projectCreatedAt = projectAssignment.createdAt instanceof Date
          ? projectAssignment.createdAt
          : projectAssignment.createdAt ? new Date(projectAssignment.createdAt as any) : undefined;
        for (const subTask of projectAssignment.subTasks) {
          // 하위 과제는 기본적으로 활성 상태로 간주
          const assignmentId = `${projectAssignment.assignmentId}-${subTask.subTaskId}`;
          
          assignments.push({
            id: assignmentId,
            title: `${projectAssignment.title} - ${subTask.subTaskTitle}`,
            type: 'project' as const,
            dueDate: new Date(projectAssignment.dueDate),
            subTaskId: subTask.subTaskId,
            subTaskTitle: subTask.subTaskTitle,
            isActive: true, // 하위 과제는 기본적으로 활성 상태
            assignedStudentIds: projectAssignment.assignedStudentIds || [],
            hasIncompleteStudents: false, // 기본값
            // 메타데이터에서 가져온 정보
            projectTitle: projectAssignment.title,
            projectDueDate: new Date(projectAssignment.dueDate),
            projectColor: projectAssignment.color,
            color: projectAssignment.color,
            basePoints: subTask.subTaskBasePoints,
            projectCreatedAt,
            subTaskOrder: subTask.subTaskOrder ?? 0
          });
          
          // 상태 데이터 저장 (중복 쿼리 방지)
          statusDataMap.set(assignmentId, {
            assignmentId: projectAssignment.assignmentId,
            subTaskId: subTask.subTaskId,
            title: projectAssignment.title,
            subTaskTitle: subTask.subTaskTitle,
            dueDate: new Date(projectAssignment.dueDate),
            assignedStudentIds: projectAssignment.assignedStudentIds,
            basePoints: subTask.subTaskBasePoints,
            studentStatuses: subTask.studentStatuses
          });
        }
      }
      
      console.log('✅ 새로운 구조: 프로젝트 과제 테이블 로드 완료:', assignments.length);
      return { assignments, statusData: statusDataMap };
    } catch (error) {
      console.error('❌ 새로운 구조: 프로젝트 과제 테이블 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 로드 (기존 방식 - 호환성 유지)
   */
  private async loadProjectAssignments(userId: string, classId: string): Promise<AssignmentInfo[]> {
    const projectAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
    const projectAssignmentsSnap = await getDocs(projectAssignmentsRef);
    firebaseReadCounter.increment(1); // 컬렉션 쿼리는 1회로 계산
    
    const assignments: AssignmentInfo[] = [];

    for (const projectDoc of projectAssignmentsSnap.docs) {
      const projectAssignment = projectDoc.data() as any;
      
      if (!projectAssignment.isActive || !projectAssignment.assignedStudentIds?.length) {
        continue;
      }

      // 하위 과제들 처리
      if (projectAssignment.subTasks && Array.isArray(projectAssignment.subTasks)) {
        for (const subTask of projectAssignment.subTasks) {
          if (subTask.isActive !== false) {
            assignments.push({
              id: `${projectDoc.id}-${subTask.id}`, // 프로젝트 ID와 하위과제 ID 조합
              title: `${projectAssignment.title} - ${subTask.title}`,
              type: 'project' as const,
              dueDate: subTask.dueDate?.toDate(),
              subTaskId: subTask.id,
              subTaskTitle: subTask.title,
              isActive: subTask.isActive !== false,
              assignedStudentIds: projectAssignment.assignedStudentIds || [],
              hasIncompleteStudents: false, // 기본값
              // 프로젝트 관련 정보 추가
              projectTitle: projectAssignment.title,
              projectDueDate: projectAssignment.dueDate?.toDate(),
              projectColor: projectAssignment.color,
              categoryId: projectAssignment.categoryId,
              color: projectAssignment.color // 카테고리 색상으로 사용
            });
          }
        }
      }
    }

    return assignments;
  }

  /**
   * 카테고리 로드
   */
  private async loadCategories(userId: string, classId: string): Promise<any[]> {
    const { assignmentCategoryService } = await import('./assignmentCategoryService');
    return assignmentCategoryService.loadCategories(userId, classId);
  }

  /**
   * 프로젝트 데이터 로드 (프로젝트 과제에서 추출)
   */
  private async loadProjects(userId: string, classId: string): Promise<any[]> {
    const projectAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
    const projectAssignmentsSnap = await getDocs(projectAssignmentsRef);
    firebaseReadCounter.increment(1); // 컬렉션 쿼리는 1회로 계산
    
    const projectMap = new Map<string, any>();
    
    projectAssignmentsSnap.docs.forEach(projectDoc => {
      const projectAssignment = projectDoc.data() as any;
      
      if (projectAssignment.isActive) {
        const projectId = projectAssignment.title.replace(/\s+/g, '-').toLowerCase();
        
        if (!projectMap.has(projectId)) {
          projectMap.set(projectId, {
            id: projectId,
            title: projectAssignment.title,
            color: projectAssignment.color || '#FC5555'
          });
        }
      }
    });
    
    return Array.from(projectMap.values());
  }

  /**
   * 루틴 과제 로드 (통합 문서 구조 사용)
   */
  private async loadRoutineAssignmentsFromStatus(
    userId: string,
    classId: string,
    selectedDate?: Date,
    memberGroup?: string
  ): Promise<{ assignments: AssignmentInfo[], statusData: Map<string, any> }> {
    // 🚀 새로운 통합 구조 사용: routineAssignmentService를 통해 로드
    const { routineAssignmentService } = await import('./routineAssignmentService');
    
    try {
      const routineAssignments = await routineAssignmentService.loadRoutineAssignments(userId, classId, memberGroup);
      firebaseReadCounter.increment(1); // 통합 문서 읽기는 1회로 계산
      
      const assignments: AssignmentInfo[] = [];
      const statusDataMap = new Map<string, any>();

      // 🚀 최적화: 선택된 날짜 또는 오늘 날짜 필터링 (한국 시간대 고려)
      const targetDate = selectedDate || new Date();
      const targetDateString = this.dateToDateId(targetDate);

      // 각 루틴 과제의 날짜 계산 및 오늘 날짜 확인
      for (const routineAssignment of routineAssignments) {
        if (!routineAssignment.isActive || !routineAssignment.assignedStudentIds?.length) {
          continue;
        }

        // 루틴 과제의 날짜들을 계산 (오늘 날짜 포함)
        const routineDates = this.calculateRoutineDates(
          routineAssignment.routineDays || [],
          routineAssignment.dueDate || new Date(),
          routineAssignment.startDate
        );

        // 선택된 날짜에 해당하는 과제만 처리
        const targetDateFound = routineDates.find(date => this.dateToDateId(date) === targetDateString);
        if (targetDateFound) {
          const routineId = routineAssignment.id;
          const dateId = this.dateToDateId(targetDateFound);
          
          // 통합 문서에서 해당 날짜의 상태 데이터 조회
          const { RoutineAssignmentConsolidatedService } = await import('./routineAssignmentConsolidatedService');
          const consolidatedService = new RoutineAssignmentConsolidatedService(userId, classId, memberGroup);
          const routineWithStatuses = await consolidatedService.loadRoutineAssignmentWithStatuses(routineId);
          firebaseReadCounter.increment(1);
          
          let statusData: any = null;
          if (routineWithStatuses && routineWithStatuses.dateStatuses) {
            const dateAssignment = routineWithStatuses.dateStatuses.find(da => da.date === dateId);
            if (dateAssignment) {
              statusData = {
                assignedStudentIds: routineAssignment.assignedStudentIds,
                basePoints: routineAssignment.basePoints,
                categoryId: routineAssignment.categoryId,
                color: routineAssignment.color || '#6366f1',
                studentStatuses: dateAssignment.studentStatuses || []
              };
            }
          }
          
          const assignmentId = `${routineId}-${dateId}`;
          const routineCreatedAt = routineWithStatuses?.routine?.createdAt
            ? (routineWithStatuses.routine.createdAt instanceof Date ? routineWithStatuses.routine.createdAt : new Date(routineWithStatuses.routine.createdAt as any))
            : undefined;
          
          assignments.push({
            id: assignmentId,
            title: `${routineAssignment.title} - ${targetDateFound.toLocaleDateString('ko-KR')}`,
            type: 'routine' as const,
            dueDate: targetDateFound,
            routineAssignmentId: routineId,
            routineTitle: routineAssignment.title,
            dateId: dateId,
            isActive: true,
            assignedStudentIds: routineAssignment.assignedStudentIds || [],
            hasIncompleteStudents: false, // 기본값
            basePoints: routineAssignment.basePoints,
            categoryId: routineAssignment.categoryId,
            color: routineAssignment.color || '#6366f1', // 루틴 과제 색상 (인디고)
            createdAt: routineCreatedAt
          });
          
          // 상태 데이터 저장 (중복 쿼리 방지)
          if (statusData) {
            statusDataMap.set(assignmentId, statusData);
          }
        }
      }
      
      console.log('✅ 새로운 구조: 루틴 과제 테이블 로드 완료:', assignments.length);
      return { assignments, statusData: statusDataMap };
    } catch (error) {
      console.error('❌ 새로운 구조: 루틴 과제 테이블 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 루틴 과제 로드 (기존 방식 - 호환성 유지)
   */
  private async loadRoutineAssignments(userId: string, classId: string): Promise<AssignmentInfo[]> {
    const routineAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'routineAssignments');
    const routineAssignmentsSnap = await getDocs(routineAssignmentsRef);
    firebaseReadCounter.increment(routineAssignmentsSnap.docs.length);
    
    const assignments: AssignmentInfo[] = [];

    for (const routineDoc of routineAssignmentsSnap.docs) {
      const routineAssignment = routineDoc.data() as any;
      
      if (!routineAssignment.isActive || !routineAssignment.assignedStudentIds?.length) {
        continue;
      }

      // 루틴 과제의 날짜들을 계산 (오늘 날짜 포함)
      const routineDates = this.calculateRoutineDates(
        routineAssignment.routineDays || [],
        routineAssignment.dueDate?.toDate() || new Date(),
        routineAssignment.startDate?.toDate()
      );

      // 🚀 최적화: 오늘 날짜만 필터링하여 과제 생성 (한국 시간대 고려)
      const today = new Date();
      const todayString = this.dateToDateId(today);
      
      // 오늘 날짜에 해당하는 과제만 생성
      const todayDate = routineDates.find(date => this.dateToDateId(date) === todayString);
      if (todayDate) {
        const dateId = this.dateToDateId(todayDate);
        
        assignments.push({
          id: `${routineDoc.id}-${dateId}`,
          title: `${routineAssignment.title} - ${todayDate.toLocaleDateString('ko-KR')}`,
          type: 'routine' as const,
          dueDate: todayDate,
          routineAssignmentId: routineDoc.id,
          routineTitle: routineAssignment.title,
          dateId: dateId,
          isActive: true,
          assignedStudentIds: routineAssignment.assignedStudentIds || [],
          hasIncompleteStudents: false, // 기본값
          categoryId: routineAssignment.categoryId,
          color: routineAssignment.color
        });
      }
    }

    return assignments;
  }

  /**
   * 루틴 과제의 날짜들을 계산하는 함수 (한국 시간대 고려)
   */
  private calculateRoutineDates(routineDays: number[], dueDate: Date, startDate?: Date): Date[] {
    const dates: Date[] = [];
    
    // 한국 시간대 기준으로 현재 날짜 계산
    const now = new Date();
    const koreaTime = new Date(now.getTime() + (9 * 60 * 60 * 1000));
    const koreaDate = new Date(koreaTime.getUTCFullYear(), koreaTime.getUTCMonth(), koreaTime.getUTCDate());
    
    // 시작일 설정: startDate가 있으면 사용, 없으면 과거 1년 전부터
    const actualStartDate = startDate || new Date(koreaDate.getFullYear() - 1, 0, 1);
    actualStartDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(dueDate);
    endDate.setHours(23, 59, 59, 999);
    
    const currentDate = new Date(actualStartDate);
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      if (routineDays.includes(dayOfWeek)) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  }

  /**
   * 날짜를 YYYY-MM-DD 형식의 ID로 변환 (한국 시간대 고려)
   */
  private dateToDateId(date: Date): string {
    // 한국 시간대 (UTC+9)로 변환
    const koreaTime = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    const dateStr = koreaTime.getUTCFullYear() + '-' + 
      String(koreaTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
      String(koreaTime.getUTCDate()).padStart(2, '0');
    return dateStr;
  }

  /**
   * 각 과제에 대해 hasIncompleteStudents 계산
   */
  private async calculateIncompleteStudents(
    allAssignments: AssignmentInfo[], 
    studentStatuses: StudentAssignmentStatus[]
  ): Promise<AssignmentInfo[]> {
    return allAssignments.map(assignment => {
      let uniqueKey = assignment.id;
      
      if (assignment.type === 'project' && assignment.subTaskId) {
        uniqueKey = `${assignment.id}-${assignment.subTaskId}`;
      } else if (assignment.type === 'routine' && assignment.dateId) {
        uniqueKey = `${assignment.routineAssignmentId}-${assignment.dateId}`;
      }

      const assignedStudents = assignment.assignedStudentIds || [];
      
      // 미완료 멤버가 있는지 확인
      const hasIncompleteStudents = assignedStudents.some(studentId => {
        // 해당 멤버의 상태 찾기
        const studentStatus = studentStatuses.find(s => s.studentId === studentId);
        if (!studentStatus) return true; // 멤버 상태가 없으면 미완료
        
        // 해당 과제의 상태 찾기
        const assignmentStatus = studentStatus.assignmentStatuses[uniqueKey];
        return !assignmentStatus || assignmentStatus.status === 'pending';
      });

      console.log(`🔍 과제 ${assignment.title} 미완료 상태 계산:`, {
        assignmentId: assignment.id,
        assignedStudentsCount: assignedStudents.length,
        hasIncompleteStudents,
        uniqueKey
      });

      return {
        ...assignment,
        hasIncompleteStudents
      };
    });
  }
}

export const assignmentTableServiceOptimizedV4 = new AssignmentTableServiceOptimizedV4();
